import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchContentRef,
} from 'react-zoom-pan-pinch';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CleaningImage from '@common/interfaces/cleaning-map/cleaning-image';
import Map from '@common/components/CleaningMap/Map';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { CrossCloseIcon } from '@common/utils/icons/icons';

const MapPopup: React.FC<{ open: boolean; cleaningImage: CleaningImage; onClose: () => void }> = ({
  open,
  cleaningImage,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const transformRef = useRef<ReactZoomPanPinchContentRef | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useLayoutEffect(() => {
    if (transformRef.current && cleaningImage.obstacleMap && isInitialized) {
      const { instance } = transformRef.current;

      const containerWidth = instance.wrapperComponent?.clientWidth || 1;
      const containerHeight = instance.wrapperComponent?.clientHeight || 1;

      const svgWidth = parseInt(theme.spacing(4), 0) + cleaningImage.obstacleMap.width;
      const svgHeight = parseInt(theme.spacing(4), 0) + cleaningImage.obstacleMap.height;

      const scale = Math.min(containerWidth / svgWidth, containerHeight / svgHeight);

      instance.setTransformState(
        scale,
        (containerWidth - svgWidth * scale) / 2,
        (containerHeight - svgHeight * scale) / 2,
      );
    }
  }, [cleaningImage.obstacleMap, isInitialized, theme]);

  return (
    <React.Fragment>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogContent
          sx={(theme) => ({
            position: 'relative',
            height: '80vh',
            backgroundColor: theme.palette.background.default,
          })}
        >
          <Box
            sx={(theme) => ({
              position: 'absolute',
              right: theme.spacing(1),
              top: theme.spacing(1),
              zIndex: theme.zIndex.fab,
            })}
          >
            <TooltipIconButton
              tooltip={t('info-tooltip-close')}
              icon={<CrossCloseIcon />}
              onClick={onClose}
            />
          </Box>

          <TransformWrapper
            limitToBounds={false}
            ref={(node) => {
              transformRef.current = node;
              setIsInitialized(true);
            }}
            initialScale={1}
            minScale={0.25}
            centerOnInit
            maxScale={10}
            wheel={{ step: 0.1 }}
          >
            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
              <Map cleaningImage={cleaningImage} disableAnimation />
            </TransformComponent>
          </TransformWrapper>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default MapPopup;
